/**
 * @file   QuickStatsStrip.js
 * @brief  Three inline stat pills for Match tab: Active Matches, Peak Demand, Most Demand range.
 *         Only renders for Tier 1-2 (3+ matches).
 */
import React, { useMemo } from "react";
import { formatCompactPrice, computePeakDemandBin, computeSweetSpotRange } from "../../../helpers/priceCapUtils";

const NUM_BINS = 12;

const QuickStatsStrip = ({ matchCount, prices, adType, rangeMin, rangeMax }) => {
  if (!prices || prices.length < 3) return null;

  // Use server-side totalCount for Active Buyers (not prices.length which is capped by page size)
  const activeBuyerCount = matchCount || prices.length;

  const peakBinIdx = useMemo(
    () => computePeakDemandBin(prices, NUM_BINS, rangeMin, rangeMax),
    [prices, rangeMin, rangeMax],
  );

  const sweetSpot = useMemo(
    () => computeSweetSpotRange(prices, adType),
    [prices, adType],
  );

  // Calculate peak demand price from bin index
  const span = rangeMax - (rangeMin || 0);
  const peakPrice = peakBinIdx != null && span > 0
    ? (rangeMin || 0) + (peakBinIdx + 0.5) * (span / NUM_BINS)
    : null;

  return (
    <div className="quick-stats-strip" data-tour="quick-stats">
      <div className="quick-stats-strip__pill">
        <span className="quick-stats-strip__value">{activeBuyerCount}</span>
        <span className="quick-stats-strip__label">Active Buyers</span>
        <span className="quick-stats-strip__sublabel">looking for properties like yours</span>
      </div>
      {peakPrice != null && (
        <div className="quick-stats-strip__pill">
          <span className="quick-stats-strip__value">{formatCompactPrice(peakPrice)}</span>
          <span className="quick-stats-strip__label">Peak Demand</span>
          <span className="quick-stats-strip__sublabel">where most buyers cluster</span>
        </div>
      )}
      {sweetSpot && (
        <div className="quick-stats-strip__pill">
          <span className="quick-stats-strip__value quick-stats-strip__value--range">
            {formatCompactPrice(sweetSpot.min)} - {formatCompactPrice(sweetSpot.max)}
          </span>
          <span className="quick-stats-strip__label">Sweet Spot</span>
          <span className="quick-stats-strip__sublabel">highest concentration of demand</span>
        </div>
      )}
    </div>
  );
};

export default QuickStatsStrip;
